import type { UseMutationResult } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { trackRecurrency, type RecurrencyEventName, type RecurrencyEventPayload } from '@cannect/services/events'
import { QUERY_KEYS } from '@cannect/services/resources/recurrency/constants'
import { getRecurrencyError, type RecurrencyErrorInfo } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'

// Erros que significam "o estado mudou": mostra o texto do servidor e recarrega.
const STATE_CODES = [
  'CYCLE_LOCKED',
  'SUBSCRIPTION_PAUSED',
  'SUBSCRIPTION_CANCELLED',
  'SUBSCRIPTION_ENDED',
  'ALREADY_PAUSED',
  'NOT_PAUSED',
  'INVALID_CYCLE_TRANSITION'
]

type ActionOptions<TVars, TResp> = {
  detail: Pick<SubscriptionDetail, 'id' | 'cycleDays'>
  mutation: UseMutationResult<TResp, unknown, TVars, unknown>
  event?: RecurrencyEventName
  eventPayload?: () => Partial<RecurrencyEventPayload>
  successMessage?: (response: TResp) => string | undefined
  fieldErrorCodes?: string[]
  onFieldError?: (err: RecurrencyErrorInfo) => void
  onErrorCode?: Record<string, (err: RecurrencyErrorInfo) => void>
  onSuccess?: (response: TResp) => void
}

// Wrapper das ações de "Minhas assinaturas": toast, analytics sem ids e o
// mapa de erros de contracts/portal-consumed-api.md.
export const useSubscriptionAction = <TVars, TResp>({
  detail,
  mutation,
  event,
  eventPayload,
  successMessage,
  fieldErrorCodes = [],
  onFieldError,
  onErrorCode = {},
  onSuccess
}: ActionOptions<TVars, TResp>) => {
  const client = useQueryClient()

  const run = async (variables: TVars) => {
    try {
      const response = await mutation.mutateAsync(variables)
      const message = successMessage?.(response) ?? (response as { message?: string } | undefined)?.message
      toast.success(message || 'Alteração salva')
      if (event) {
        trackRecurrency(event, { origin: 'my_subscriptions', cycleDays: detail.cycleDays, ...eventPayload?.() })
      }
      onSuccess?.(response)
      return true
    } catch (err) {
      const info = getRecurrencyError(err)

      if (info.code && onErrorCode[info.code]) {
        onErrorCode[info.code](info)
      } else if (info.code && fieldErrorCodes.includes(info.code) && onFieldError) {
        onFieldError(info)
      } else if (info.code === 'LOCK_BUSY' || info.status === 409) {
        toast.error(subscriptionsCopy.errors.lockBusy)
      } else if (info.partial) {
        toast.error(subscriptionsCopy.errors.partial)
        client.invalidateQueries({ queryKey: QUERY_KEYS.all })
      } else if (info.code && STATE_CODES.includes(info.code)) {
        toast.error(info.message)
        client.invalidateQueries({ queryKey: QUERY_KEYS.subscription(detail.id) })
      } else {
        toast.error(info.message)
      }
      return false
    }
  }

  return { run, isPending: mutation.isPending }
}
